import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { ArrowSquareOut, LockSimple, WarningCircle, CheckCircle, Phone, MapPin } from "@phosphor-icons/react";
import { useNoIndex } from "../../hooks/usePageSeo";
import { functions } from "../../firebase";
import LoaderLoop from "../../components/shared/loader/LoaderLoop";
import "./GrowPublic.css";

const TERMS_PATH = "/pay/terms";

/**
 * /pay/:code — short CliniKit link sent to the patient (WhatsApp URL button /
 * SMS). Interstitial required by Grow's site check (2026-11-03): shows what is
 * being paid, the merchant (clinic) phone + address, and a terms checkbox that
 * must be ticked before the Grow hosted page opens. No Firestore access from
 * the page; the public callable returns no payer data.
 * Error codes: CK-6300 (callable), CK-6303 (redirect), CK-6304 (sessionStorage)
 */
const fmtMoney = (n) => `₪${Number(n || 0).toLocaleString("he-IL", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

function readConsent(code) {
  try {
    return window.sessionStorage.getItem(`ck-pay-terms-${code}`) === "1";
  } catch (error) {
    console.error("[CK-6304] Grow pay consent read failed", { errorCode: "CK-6304", errorMessage: error.message, timestamp: new Date().toISOString() });
    return false;
  }
}

function writeConsent(code, value) {
  try {
    if (value) window.sessionStorage.setItem(`ck-pay-terms-${code}`, "1");
    else window.sessionStorage.removeItem(`ck-pay-terms-${code}`);
  } catch (error) {
    console.error("[CK-6304] Grow pay consent write failed", { errorCode: "CK-6304", errorMessage: error.message, timestamp: new Date().toISOString() });
  }
}

export default function GrowPayPage() {
  useNoIndex();
  const { code } = useParams();
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [accepted, setAccepted] = useState(() => readConsent(code));
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await httpsCallable(functions, "resolveGrowPayLinkPublic")({ code });
        if (cancelled) return;
        setState({ loading: false, error: null, data: res.data || {} });
      } catch (error) {
        console.error("[CK-6300] Grow pay link resolve failed", {
          errorCode: "CK-6300",
          errorMessage: error.message,
          code: error.code,
          timestamp: new Date().toISOString(),
        });
        if (!cancelled) setState({ loading: false, error: error.code === "functions/not-found" ? "not_found" : "error", data: null });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [code]);

  const d = state.data;
  const payable = !!(d?.active && d.url && Number(d.sum) > 0);
  const products = Array.isArray(d?.products) ? d.products : [];
  const termsUrl = d?.termsUrl || TERMS_PATH;

  const onToggle = (e) => {
    setAccepted(e.target.checked);
    writeConsent(code, e.target.checked);
  };

  const onPay = () => {
    if (!accepted || !payable || redirecting) return;
    try {
      setRedirecting(true);
      window.location.assign(d.url);
    } catch (error) {
      console.error("[CK-6303] Grow pay redirect failed", { errorCode: "CK-6303", errorMessage: error.message, timestamp: new Date().toISOString() });
      setRedirecting(false);
    }
  };

  return (
    <main className="gpub" dir="rtl">
      <section className="gpub-card" aria-live="polite">
        {d?.logoUrl ? <img className="gpub-logo" src={d.logoUrl} alt="" /> : <span className="gpub-logo-fallback"><LockSimple size={26} weight="fill" /></span>}
        <span className="gpub-eyebrow">{d?.clinicName || "CliniKit"}</span>

        {state.loading && (
          <>
            <h1>טוענים את פרטי התשלום…</h1>
            <LoaderLoop size="md" />
          </>
        )}

        {!state.loading && payable && (
          <>
            <h1>תשלום ל{d.clinicName || "קליניקה"}</h1>

            <div className="gpub-amount" aria-label="סכום לתשלום">
              <span className="gpub-amount-label">סכום לתשלום</span>
              <span className="gpub-amount-value">{fmtMoney(d.sum)}</span>
            </div>

            {products.length > 0 && (
              <ul className="gpub-items" aria-label="פירוט">
                {products.map((p, i) => (
                  <li key={`${p.name}-${i}`}>
                    <span className="gpub-item-name">
                      {p.name}
                      {Number(p.quantity) > 1 ? <span className="gpub-item-qty"> ×{p.quantity}</span> : null}
                    </span>
                    <span className="gpub-item-price">{fmtMoney(p.price)}</span>
                  </li>
                ))}
              </ul>
            )}

            {(d.clinicPhone || d.clinicAddress) && (
              <div className="gpub-merchant">
                <span className="gpub-merchant-title">בית העסק: {d.clinicName}</span>
                {d.clinicPhone && (
                  <a className="gpub-merchant-row" href={`tel:${d.clinicPhone}`}>
                    <Phone size={14} weight="fill" />
                    <span dir="ltr">{d.clinicPhone}</span>
                  </a>
                )}
                {d.clinicAddress && (
                  <span className="gpub-merchant-row">
                    <MapPin size={14} weight="fill" />
                    <span>{d.clinicAddress}</span>
                  </span>
                )}
              </div>
            )}

            <label className="gpub-consent" htmlFor="gpub-terms">
              <input id="gpub-terms" type="checkbox" checked={accepted} onChange={onToggle} />
              <span>
                קראתי ואני מאשר/ת את{" "}
                <a href={termsUrl} target="_blank" rel="noreferrer">תנאי התשלום והתקנון</a>
              </span>
            </label>

            <button
              type="button"
              className="gpub-btn"
              onClick={onPay}
              disabled={!accepted || redirecting}
              aria-disabled={!accepted || redirecting}
            >
              {redirecting ? <LoaderLoop size="sm" /> : <ArrowSquareOut size={16} />}
              {redirecting ? "מעבירים לתשלום…" : "לתשלום מאובטח"}
            </button>
            {!accepted && <span className="gpub-hint">יש לאשר את התנאים כדי להמשיך לתשלום</span>}
            <span className="gpub-secure"><LockSimple size={13} weight="fill" /> סליקה מאובטחת באמצעות Grow</span>
          </>
        )}

        {!state.loading && d && d.active && !payable && (
          <>
            <span className="gpub-icon warn"><WarningCircle size={40} weight="fill" /></span>
            <h1>משהו השתבש</h1>
            <p>לא הצלחנו להציג את פרטי התשלום. נסו שוב בעוד רגע או פנו לקליניקה.</p>
          </>
        )}

        {!state.loading && d && !d.active && d.status === "paid" && (
          <>
            <span className="gpub-icon ok"><CheckCircle size={40} weight="fill" /></span>
            <h1>התשלום הזה כבר התקבל</h1>
            <p>תודה! אין צורך לשלם שוב.</p>
          </>
        )}

        {!state.loading && d && !d.active && d.status !== "paid" && (
          <>
            <span className="gpub-icon warn"><WarningCircle size={40} weight="fill" /></span>
            <h1>הקישור אינו פעיל</h1>
            <p>דרישת התשלום בוטלה או שתוקפה פג. לקבלת קישור חדש פנו לקליניקה.</p>
          </>
        )}

        {!state.loading && state.error && (
          <>
            <span className="gpub-icon warn"><WarningCircle size={40} weight="fill" /></span>
            <h1>{state.error === "not_found" ? "הקישור לא נמצא" : "משהו השתבש"}</h1>
            <p>{state.error === "not_found" ? "בדקו שהקישור הועתק במלואו, או פנו לקליניקה." : "נסו שוב בעוד רגע. אם הבעיה חוזרת, פנו לקליניקה."}</p>
          </>
        )}

        {!state.loading && (
          <a className="gpub-footlink" href={termsUrl} target="_blank" rel="noreferrer">תנאי תשלום</a>
        )}
      </section>
    </main>
  );
}
